import React, { useRef } from "react";
import { JsonLd } from "react-schemaorg";
import { Article } from "schema-dts";
import { AspirerAcquisitionCampaignControl } from "web/react/components/aspirer-acquisition-campaign-control/aspirer-acquisition-campaign-control";
import { CustomerCaptureOverlay } from "web/react/components/customer-capture-overlay/customer-capture-overlay";
import { EditorialShoppable } from "web/react/components/editorials/editorial-shoppable";
import { Header } from "web/react/components/editorials/header";
import { IntroBlock } from "web/react/components/editorials/intro-block";
import { ProductOverlay } from "web/react/components/product-overlay";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { useIntersectionObserver } from "web/react/hooks/use-intersection-observer/use-intersection-observer";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import { EditorialSerializer } from "web/types/serializers";
import * as styles from "./editorial.css";

interface EditorialProps {
    editorial: EditorialSerializer;
}

export function Editorial({ editorial }: EditorialProps): React.ReactElement {
    const {
        header,
        is_sponsored: isSponsored,
        intro,
        shoppable,
        gender,
        editorial_label: editorialLabel,
        franchise,
        tags,
    } = editorial;

    const triggerModalRef = useRef<HTMLDivElement>(null);
    const [triggerModal] = useIntersectionObserver(
        {
            threshold: 0,
            rootMargin: "0px",
            once: true,
        },
        triggerModalRef
    );

    const editorialMetaInfo = { editorialLabel, franchise, tags, isSponsored, gender };

    const overlayRef = useRef(null);

    let position = 0;

    const useModal = environment.getFeature(
        "crm_signup_modal_on_editorial_and_curated_collections"
    );

    return (
        <div>
            <CustomerCaptureOverlay />

            {useModal && !isSponsored && (
                <AspirerAcquisitionCampaignControl isVisible={triggerModal} />
            )}

            <div ref={overlayRef}>
                <ProductOverlay parentRef={overlayRef} pageType="editorial" />
            </div>

            <div className={styles.wrapper}>
                <div className={styles.margin} />
                <div className={styles.contentContainer}>
                    <VStack>
                        <VStack spacing="lg_v2">
                            <Header {...header} />
                            <IntroBlock
                                position={(position += 1)}
                                {...intro}
                                {...editorialMetaInfo}
                            />
                        </VStack>
                        <div ref={triggerModalRef} />
                        <div className={styles.shoppableContainer}>
                            {shoppable.map((shoppable, key) => (
                                <EditorialShoppable
                                    key={`${key}-${shoppable}-${position}`}
                                    position={(position += 1)}
                                    {...shoppable}
                                    {...editorialMetaInfo}
                                />
                            ))}
                        </div>
                    </VStack>
                </div>
                <div className={styles.margin}>
                    <div className={styles.advertorialText}>
                        <span className={styles.smallDash}>{"\u2E3A"}</span>
                        <Text color="secondary" dataTestId="margin-text" textStyle="caption-3">
                            {isSponsored
                                ? gettext("editorial.advertorial_tag") // Advertorial / Editorial
                                : gettext("editorial.editorial_tag")}
                        </Text>
                    </div>
                </div>
            </div>
            <JsonLd<Article>
                item={{
                    "@context": "https://schema.org",
                    "@type": "Article",
                    author: header.authors || "",
                    headline: header.title,
                    datePublished: header.published_at || "",
                    dateModified: header.updated_at || "",
                    image: intro.image_url || "",
                }}
            />
        </div>
    );
}
